import { cloneDeep, get } from 'lodash';
import React, { ReactNode } from 'react';
import IconButton from 'shared/buttons/IconButton';
import { GenericInputProps, InputChangeParams } from 'shared/types/commonPropTypes';
import { OptionProps, WidgetType } from 'pages/ChartEditorPage/meta/CustomizeOptions';
import fieldsMap from './FieldsMap';
import classNames from 'classnames';
import SortableItem from './SortableItem';
import SortableContainer from './SortableContainer';
import PlusIcon from './PlusIcon';

const EmptyContainer = (props: { children: ReactNode; values?: []; id?: string | number }) => <>{props.children}</>;

const ArrayWidget = (props: GenericInputProps) => {
  const { value, option } = props;
  const showAsRows = option?.custom?.showAsRows;
  const openIfOne = option?.custom?.openIfOne;
  const type = option?.type;
  let subtype = 'color';

  if (type && type.indexOf('array') === 0) {
    subtype = type.substr(6, type.length - 7);
  }

  const getValue = () => {
    const { option } = props;
    if (option?.isBasedOnSeriesData) {
      const { aggregatedOptions } = props;
      if (aggregatedOptions.series[0].data) {
        let val = aggregatedOptions.series[0].data;
        val = (val || []).map((d: any) => {
          return get(d, option?.id ?? '');
        });
        return val;
      }
    }
    return value;
  };

  const onChange = (params: InputChangeParams) => {
    let arrayValue = cloneDeep(getValue());
    if (!arrayValue) {
      arrayValue = Array((params.index ?? 0) + 1).fill(null);
    }
    arrayValue[params.index as number] = params.val as unknown;
    props.onChange({ val: arrayValue });
  };

  const addOption = () => {
    const newValue = cloneDeep(value ?? []);
    (newValue as string[]).push('');
    props.onChange({ val: newValue });
  };

  const deleteOption = (index: number) => {
    const newValue = (value as string[]).filter((_, i) => i !== index);
    props.onChange({ val: newValue });
  };

  const parsedValue = getValue();
  const InputType = (fieldsMap[subtype as WidgetType] || fieldsMap.default) as React.ElementType;
  const onlyOneSection = (parsedValue ?? []).length === 1;
  const displayInternalLabel = subtype === 'string' || (subtype !== 'string' && !(parsedValue ?? []).length);
  const displayFlex = !['icon', 'accordion'].includes(subtype);

  const className = classNames('color-row', {
    'content-center flex-col': showAsRows,
    'flex-row w-full max-w-full': !showAsRows,
    flex: displayFlex
  });

  const getLabel = (option: OptionProps | undefined, index: number) => {
    return displayInternalLabel ? null : `${option?.title ?? 'Series'} ${index + 1}`;
  };

  const getContainers = (option: OptionProps | undefined) => {
    const isSortable = option?.custom?.isSortable;
    return {
      RootContainer: isSortable ? SortableContainer : EmptyContainer,
      ItemContainer: isSortable ? SortableItem : EmptyContainer
    };
  };

  const { RootContainer, ItemContainer } = getContainers(option);

  return (
    <div className={`highed-field-table flex ${showAsRows ? '' : 'flex-col gap-y-2'}`}>
      {displayInternalLabel && <div className="text-ev-navy-blue font-bold mt-2">{option?.title ?? 'Series'}</div>}

      <RootContainer value={parsedValue} onChange={props.onChange}>
        {(parsedValue || []).map((val: string, index: number) => {
          const parentPath = (option?.rawKey ?? '').replace('[n]', `[${index}]`);
          const label = getLabel(option, index);

          return (
            <ItemContainer id={index + 1} key={`array_${index}`}>
              <div className={className}>
                <InputType
                  {...props}
                  value={val}
                  index={index}
                  isArraySelector={true}
                  onChange={onChange}
                  label={label}
                  parentPath={parentPath}
                  open={openIfOne && onlyOneSection}
                  className={`${props.className ?? ''}${displayInternalLabel ? ' w-full' : ''}`}
                />
                {!option?.isBasedOnSeriesData && !option?.custom?.hideDeleteButtons && showAsRows && (
                  <IconButton onClick={() => deleteOption(index)} icon="fa fa-trash-can" className="my-1" />
                )}

                {!option?.isBasedOnSeriesData && !option?.custom?.hideDeleteButtons && !showAsRows && (
                  <IconButton icon={'trash'} onClick={() => deleteOption(index)} className="rounded-sm ml-2 p-2 px-3" />
                )}
              </div>
            </ItemContainer>
          );
        })}
      </RootContainer>

      {!option?.isBasedOnSeriesData && !option?.custom?.hideAddButton && (
        <PlusIcon onClick={addOption} className={showAsRows ? '' : 'mx-auto mb-4'} />
      )}
    </div>
  );
};

export default ArrayWidget;
